import React, { useEffect } from "react";
import loadable from "@loadable/component";
import { useBnbSelector } from "../../redux/store";
import { NavStep } from "../../../shared-logic/utils/Navigation";
import ModelOverview from "./step-model/ModelOverview";
import GradesOverview from "./step-grade-engine/GradesOverview";
import BuildNavigationItems from "./common/BuildNavigationItems";
import { useModalMap } from "../../../../../common-deprecated/containers/ModalProvider";
import { aemToyotaBnbModalMap } from "./modals/modals";
import SelectEngine from "./step-engine/SelectEngine";
import useInit from "../../hooks/shared/init/useInit";
import CustomizeContainer from "./step-customize/CustomizeContainer";
import useStatus from "../../../../../common-deprecated/hooks/useStatus";
import ServicesContainer from "./step-services/ServicesContainer";
import BuyContainer from "./step-buy/BuyContainer";
import BuildNavigation from "./common/nav/BuildNavigation";
import SummaryContainer from "./step-summary/SummaryContainer";
import MobileFooter from "./common/nav/MobileFooter";
import { MODAL_FINANCE_INFO, MODAL_LCV_DEBUG } from "../../utils/modalConstants";
import ReserveContainer from "./step-reserve/ReserveContainer";
import { enableCheckoutSteps, showServicesStep } from "../../utils/settings/bnbSettingUtils";
import useBuildTools from "../../hooks/useBuildTools";
import { getRetailerDebugToggleLink, getThemeClassname } from "../../../../../common-deprecated/utils";
import LeaseView from "./step-lease/LeaseView";
import { buildFetchDispatcher, getBuildComponentFetches } from "../../datafetch/fetchUtils";
import { COMPONENT_BUILDANDBUY } from "../../../../../shared-logic/server/components";
import useDataFetcher from "../../../../../common-deprecated/hooks/useDataFetcher";
import { bnbPostFetch } from "../../datafetch/postFetch";
import type { GetFetchesFnType } from "../../../../../common-deprecated/utils/registryTypes";
import NotificationContainer from "../../../../../common-deprecated/components/notification/toyota/NotificationContainer";
import GlobalStyle from "../../../../../common-deprecated/styles/v2/common/GlobalStyle";
import { getModelIdSelector } from "../../utils/typedUtils";
import { updateTrackingLayerVersioning } from "../../utils/tracking";

const IntegratedCompareV2 = loadable(async () => {
    const component = await import(
        /* webpackChunkName: "integrated-compare-aem-toyota" */
        "../../../../compare/components/toyota/IntegratedCompareToyota"
    );
    return component.default;
});

const BuildAndBuyV2 = (): JSX.Element => {
    useInit();
    useModalMap(aemToyotaBnbModalMap);

    useEffect(() => {
        updateTrackingLayerVersioning();
    }, []);

    const step = useBnbSelector((state) => state.navigation.step);
    const bnbSettings = useBnbSelector((state) => state.bnbSettings);
    const modelId = useBnbSelector(getModelIdSelector);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    const retailerSettings = useBnbSelector((state) => state.retailerSettings);
    const showServices = showServicesStep(bnbSettings, retailerSettings);
    const stepTotal = 5 - (showServices ? 0 : 1) - (enableCheckoutSteps(bnbSettings, retailerSettings) ? 0 : 1);

    useDataFetcher(
        getBuildComponentFetches as unknown as GetFetchesFnType,
        buildFetchDispatcher,
        COMPONENT_BUILDANDBUY,
        [],
        [],
        bnbPostFetch,
    );

    const buildLinks = useBuildTools();
    useStatus(
        commonSettings,
        "AEM Toyota Build",
        [...buildLinks, getRetailerDebugToggleLink()],
        [
            { key: "f", settings: { type: MODAL_FINANCE_INFO } },
            { key: "l", settings: { type: MODAL_LCV_DEBUG } },
        ],
    );

    const stepNumbers: Partial<Record<NavStep, number>> = {
        [NavStep.Model]: 1,
        [NavStep.Engine]: 1,
        [NavStep.Customize]: 2,
        [NavStep.Services]: showServices ? 3 : 0,
        [NavStep.Summary]: showServices ? 4 : 3,
        [NavStep.Buy]: showServices ? 5 : 4,
        [NavStep.Reserve]: showServices ? 5 : 4,
        [NavStep.Lease]: showServices ? 5 : 4,
    };

    const ModelComponent = bnbSettings.showMergedModelStep ? <GradesOverview /> : <ModelOverview />;

    let component;
    switch (step) {
        case NavStep.Model:
            component = ModelComponent;
            break;

        case NavStep.Engine:
            component = <SelectEngine />;
            break;

        case NavStep.Customize:
            component = <CustomizeContainer />;
            break;

        case NavStep.Services:
            component = <ServicesContainer />;
            break;

        case NavStep.Summary:
            component = <SummaryContainer />;
            break;

        case NavStep.Buy:
            component = <BuyContainer />;
            break;

        case NavStep.Reserve:
            component = <ReserveContainer />;
            break;

        case NavStep.Lease:
            component = <LeaseView />;
            break;
    }

    return (
        <div className={getThemeClassname(modelId)}>
            <GlobalStyle />
            <BuildNavigation stepNumbers={stepNumbers} stepTotal={stepTotal}>
                <BuildNavigationItems showServices={showServices} />
            </BuildNavigation>
            {component}
            <MobileFooter />
            {[NavStep.Model, NavStep.Engine].includes(step) && <IntegratedCompareV2 />}
            <NotificationContainer />
        </div>
    );
};

export default BuildAndBuyV2;
